import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API, useAuth } from "@/App";
import { DiscardConfirm } from "@/components/DiscardConfirm";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  RadioGroup, RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Plus, RefreshCw, Eye, Search, ArrowLeft, ChevronRight, ChevronLeft, Calendar as CalendarIcon, Paperclip, FileCheck, FileX, MoreVertical, Pencil, Trash2, Filter, ClipboardList, LogOut, History, FileText } from "lucide-react";
import { formatDate, formatTime, formatISTTimestamp } from "@/lib/dateTime";
import { CustomerNameFields } from "@/components/CustomerNameFields";
import { resolveCustomerNames } from "@/lib/customerName";
import { PageHeader } from "@/components/PageHeader";
import { useColumnManager } from "@/hooks/useColumnManager";
import { ColumnModals } from "@/components/ColumnModals";
import { can, canCR } from "@/lib/permissions";

const PMS_INTERVALS = ["30K PMS", "40K PMS", "50K PMS", "60K PMS", "70K PMS", "80K PMS", "90K PMS", "100K PMS", "110K PMS", "120K PMS", "130K PMS", "140K PMS", "150K PMS", "160K PMS", "170K PMS", "180K PMS", "190K PMS", "200K PMS"];
const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const fmtLabel = (d) => `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`;

const SOURCES = ["Walk-in", "Appointment", "RSA"];
const STATUSES = ["Pending Contact Validation", "Validated", "Documents Pending", "Completed"];
const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "custom", label: "Custom" },
];

// Reception register columns. `gate_in` replaces the old "Vehicle Reception",
// `gate_out` is new, and the standalone "Entry Time" column is dropped
// (entry_time is now a system stamp shown only inside the detail view).
const RECEPTION_COLUMNS = [
  { id: "gate_in",        label: "Gate In" },
  { id: "gate_out",       label: "Gate Out" },
  { id: "source",         label: "Source" },
  { id: "reg_no",         label: "Reg No" },
  { id: "vin",            label: "VIN" },
  { id: "model",          label: "Model" },
  { id: "customer",       label: "Customer" },
  { id: "phone",          label: "Phone" },
  { id: "contact_valid",  label: "Contact Valid." },
  { id: "documents",      label: "Documents" },
  { id: "status",         label: "Status" },
  { id: "actions",        label: "" },
];
// Customize-Columns toggle set — Gate In, Reg No, Customer, Status, actions
// stay pinned so the register never loses its core identity.
const CUSTOMIZABLE_RECEPTION_COLUMN_IDS = [
  "gate_out", "source", "vin", "model", "phone", "contact_valid", "documents",
];

export default function Reception() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Matrix permissions — create needs reception.update, edit needs
  // reception.edit, delete needs master. Gate Out/Update Documents live
  // inside a new "Update" dropdown on the detail page and use reception.update.
  const canUpdateReception = canCR(user, "reception", "update");
  const canEditReception = canCR(user, "reception", "edit");
  const canMasterReception = can(user, "reception", "master");

  // Column visibility + order (persisted per-user under the "reception" key)
  const columnManager = useColumnManager(
    "reception",
    RECEPTION_COLUMNS,
    CUSTOMIZABLE_RECEPTION_COLUMN_IDS
  );
  const { visibleCols } = columnManager;

  // Register state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  // Reception tabs: "current" (today + in workshop) | "history"
  const [activeTab, setActiveTab] = useState("current");
  const [workshopEntries, setWorkshopEntries] = useState([]);
  const [workshopLoading, setWorkshopLoading] = useState(true);
  // History tab controls
  const [historyDateField, setHistoryDateField] = useState("entry_time");
  const [dateMode, setDateMode] = useState("date");
  const [anchorDate, setAnchorDate] = useState(() => toISODate(new Date()));
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [branches, setBranches] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ branch: "", date_filter: "today", source: "", status: "", date_from: "", date_to: "" });

  // Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [step, setStep] = useState(1);

  // Detail view state
  const [detailEntry, setDetailEntry] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);

  // Update-menu dialogs (detail page only)
  const [gateOutOpen, setGateOutOpen] = useState(false);
  const [gateOutSaving, setGateOutSaving] = useState(false);
  const [gatepassNumber, setGatepassNumber] = useState("");
  const [docUpdateOpen, setDocUpdateOpen] = useState(false);
  const [docUpdateSaving, setDocUpdateSaving] = useState(false);
  const [docUpdateData, setDocUpdateData] = useState(null);

  // Step 1 state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(-1); // Store index instead of object reference
  const [selectedVin, setSelectedVin] = useState(""); // Separate state for VIN input when vehicle is missing VIN
  const [selectedVinMissing, setSelectedVinMissing] = useState(false); // Track if VIN was originally missing
  const [selectedRegNo, setSelectedRegNo] = useState(""); // Separate state for Reg No input when missing
  const [selectedRegNoMissing, setSelectedRegNoMissing] = useState(false); // Track if Reg No was originally missing
  const [createNew, setCreateNew] = useState(false);
  const [newVehicle, setNewVehicle] = useState({ vehicle_reg_no: "", vin: "", engine_no: "" });
  const [dupError, setDupError] = useState({ reg: false, vin: false });
  const [receptionTime, setReceptionTime] = useState("");
  const [savingEntry, setSavingEntry] = useState(false);
  const savingEntryRef = useRef(false);
  const deletingEntryRef = useRef(false);
  const [deletingEntry, setDeletingEntry] = useState(false);
  const [step2Errors, setStep2Errors] = useState({});
  const [entrySource, setEntrySource] = useState("Walk-in");
  // Locks Reception Time & Source on Step 2 when the wizard was auto-opened
  // via an Appointment "Reported" hand-off (see useEffect below). Normal
  // walk-in flow leaves this false so the fields stay editable.
  const [isFromAppointment, setIsFromAppointment] = useState(false);

  // Computed: get selected vehicle from index
  const selectedVehicle = selectedVehicleIndex >= 0 ? searchResults[selectedVehicleIndex] : null;

  // Step 2 state
  const [customerType, setCustomerType] = useState("Individual");
  const [contactData, setContactData] = useState({
    salutation: "Mr",
    first_name: "", last_name: "", company_name: "",
    address: "", city: "", state: "", pin: "",
    contact_no: "", alternate_no: "", email: "",
    date_of_birth: "", anniversary: "",
    driven_by: "Owner",
    preferred_contact_mode: [],
    preferred_contact_time: [],
  });
  const [oldContactData, setOldContactData] = useState(null);

  // Step 3 state
  const [docs, setDocs] = useState({
    insurance_attached: false, insurance_not_collected: false, insurance_reason: "",
    rc_attached: false, rc_not_collected: false, rc_reason: "",
  });
  // Files chosen in Step 3, held in memory until the entry is saved.
  const [docFiles, setDocFiles] = useState({ insurance: null, rc: null });
  // Step 3 — Other Details
  const [otherData, setOtherData] = useState({
    service_type: "", pms_interval: "", allocated_sa: "", current_km: "", specific_repair_request: "",
    ots_recall: false, priority_customer: false, recovered_lost_customer: false,
  });

  const anchorObj = new Date(`${anchorDate}T00:00:00`);
  let rangeFrom = anchorDate, rangeTo = anchorDate;
  if (dateMode === "month") {
    rangeFrom = toISODate(new Date(anchorObj.getFullYear(), anchorObj.getMonth(), 1));
    rangeTo = toISODate(new Date(anchorObj.getFullYear(), anchorObj.getMonth() + 1, 0));
  } else if (dateMode === "year") {
    rangeFrom = `${anchorObj.getFullYear()}-01-01`;
    rangeTo = `${anchorObj.getFullYear()}-12-31`;
  } else if (dateMode === "custom") {
    rangeFrom = customFrom;
    rangeTo = customTo;
  }

  const stepAnchor = (dir) => {
    const d = new Date(`${anchorDate}T00:00:00`);
    if (dateMode === "month") d.setMonth(d.getMonth() + dir);
    else if (dateMode === "year") d.setFullYear(d.getFullYear() + dir);
    else d.setDate(d.getDate() + dir);
    setAnchorDate(toISODate(d));
  };

  const anchorLabel = () => {
    if (dateMode === "month") return `${MONTH_NAMES[anchorObj.getMonth()]} ${anchorObj.getFullYear()}`;
    if (dateMode === "year") return `${anchorObj.getFullYear()}`;
    return `${pad2(anchorObj.getDate())}-${pad2(anchorObj.getMonth() + 1)}-${anchorObj.getFullYear()}`;
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.branch) params.set("branch", filters.branch);
      if (filters.source) params.set("source", filters.source);
      if (filters.status) params.set("status", filters.status);
      if (activeTab === "current") {
        params.set("view", "current");
      } else if (rangeFrom && rangeTo) {
        params.set("date_filter", "custom");
        params.set("date_from", rangeFrom);
        params.set("date_to", rangeTo);
        params.set("date_field", historyDateField);
      }
      const res = await axios.get(`${API}/reception?${params.toString()}`, { withCredentials: true });
      setEntries(res.data);
    } catch { toast.error("Failed to load reception entries"); }
    setLoading(false);
  }, [filters, activeTab, rangeFrom, rangeTo, historyDateField]);

  const fetchWorkshop = useCallback(async () => {
    setWorkshopLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("view", "in_workshop");
      if (filters.branch) params.set("branch", filters.branch);
      const res = await axios.get(`${API}/reception?${params.toString()}`, { withCredentials: true });
      setWorkshopEntries(res.data);
    } catch { toast.error("Failed to load In Workshop list"); }
    setWorkshopLoading(false);
  }, [filters.branch]);

  const fetchBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branches`, { withCredentials: true });
      setBranches(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);
  useEffect(() => {
    axios.get(`${API}/settings`, { withCredentials: true })
      .then(r => setSettings(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { fetchEntries(); }, [fetchEntries]);
  useEffect(() => { fetchWorkshop(); }, [fetchWorkshop]);

  // Reload current detail entry + activity logs (used after any Update action).
  // Explicit sequential setState calls (entry then logs) so React batches
  // together and the Activity card + Timings card refresh atomically.
  const reloadDetail = useCallback(async (id) => {
    const entryId = id || detailEntry?.entry_id;
    if (!entryId) return;
    try {
      const entryRes = await axios.get(`${API}/reception/${entryId}`, { withCredentials: true });
      setDetailEntry(entryRes.data);
      const logsRes = await axios.get(`${API}/reception/${entryId}/activity`, { withCredentials: true });
      setActivityLogs(logsRes.data || []);
    } catch { toast.error("Failed to load entry"); }
  }, [detailEntry]);

  // Small helper — refetch just the activity feed for the current entry.
  const refetchActivity = useCallback(async (id) => {
    const entryId = id || detailEntry?.entry_id;
    if (!entryId) return;
    try {
      const res = await axios.get(`${API}/reception/${entryId}/activity`, { withCredentials: true });
      setActivityLogs(res.data || []);
    } catch { /* ignore */ }
  }, [detailEntry]);

  // Detail view via URL param
  useEffect(() => {
    if (entryId) {
      Promise.all([
        axios.get(`${API}/reception/${entryId}`, { withCredentials: true }),
        axios.get(`${API}/reception/${entryId}/activity`, { withCredentials: true }),
      ])
        .then(([entryRes, logsRes]) => {
          setDetailEntry(entryRes.data);
          setActivityLogs(logsRes.data || []);
          setDetailOpen(true);
        })
        .catch(() => toast.error("Entry not found"));
    }
  }, [entryId]);

  // Also fetch activity logs whenever a detail is opened from a row click
  // (rows call `setDetailEntry` + `setDetailOpen` directly, without a URL nav).
  useEffect(() => {
    if (detailOpen && detailEntry?.entry_id) {
      axios.get(`${API}/reception/${detailEntry.entry_id}/activity`, { withCredentials: true })
        .then((res) => setActivityLogs(res.data || []))
        .catch(() => setActivityLogs([]));
    } else if (!detailOpen) {
      setActivityLogs([]);
    }
    // Only re-run when the entry id changes or the panel toggles.
     
  }, [detailOpen, detailEntry?.entry_id]);

  // Auto-open the wizard on Step 2, pre-filled, when routed here from an
  // appointment marked "Reported" (AppointmentDetail → Update Status → Reported).
  // Reuses the existing prefill + canonical-customer-lookup path via selectVehicle;
  // handleSave then picks up `linked_appointment_id` from `selectedVehicle.appointment_id`.
  useEffect(() => {
    const appt = location.state?.fromAppointment;
    if (!appt) return;

    // Build a single synthetic search result matching the shape selectVehicle expects.
    // engine_no isn't on the appointment doc — saves blank per user's accepted limitation.
    const synthetic = {
      source: "appointment",
      vehicle_reg_no: appt.vehicle_reg_no || "",
      vin: appt.vin || "",
      engine_no: "",
      model: appt.model || "",
      vehicle_model: appt.model || "",
      salutation: appt.salutation || "Mr",
      customer_type: appt.customer_type || "Individual",
      first_name: appt.first_name || "",
      last_name: appt.last_name || "",
      company_name: appt.company_name || "",
      customer_name: appt.customer_name || "",
      customer_phone: appt.customer_phone || "",
      customer_email: appt.customer_email || "",
      appointment_id: appt.appointment_id || "",
    };
    setSearchResults([synthetic]);
    setSearched(true);
    // Pass the synthetic directly to selectVehicle so we don't race React state.
    selectVehicle(0, synthetic);
    setEntrySource("Appointment");
    setIsFromAppointment(true);
    // datetime-local input format — current time in the user's local zone.
    const now = new Date();
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    setReceptionTime(new Date(now - tzOffsetMs).toISOString().slice(0, 16));
    setStep(2);
    setWizardOpen(true);
    // Clear router state so a refresh / back-nav doesn't re-trigger this.
    navigate(location.pathname, { replace: true, state: {} });
  }, []);

  // Search vehicle
  // /search-vehicle returns the canonical vehicles row AND any appointment rows
  // for the same vehicle. Collapse them to one row per vehicle: canonical data
  // for the label, appointment_id kept so linked_appointment_id still saves.
  const mergeSearchResults = (rows) => {
    const groups = new Map();
    (rows || []).forEach((r, i) => {
      const reg = (r.vehicle_reg_no || "").toUpperCase().replace(/\s/g, "");
      const vin = (r.vin || "").toUpperCase().replace(/\s/g, "");
      const key = reg || vin || `__row_${i}`;
      const existing = groups.get(key);
      if (!existing) {
        groups.set(key, { ...r });
        return;
      }
      if (existing.source !== "vehicle_db" && r.source === "vehicle_db") {
        groups.set(key, { ...r, appointment_id: existing.appointment_id || "" });
        return;
      }
      if (!existing.appointment_id && r.appointment_id) {
        existing.appointment_id = r.appointment_id;
      }
    });
    return Array.from(groups.values());
  };

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) { toast.error("Enter at least 2 characters"); return; }
    setSearching(true); setSearched(true); setCreateNew(false);
    // Reset selection state
    setSelectedVehicleIndex(-1);
    setSelectedVin("");
    setSelectedVinMissing(false);
    setSelectedRegNo("");
    setSelectedRegNoMissing(false);
    try {
      const res = await axios.get(`${API}/reception/search-vehicle?q=${encodeURIComponent(searchQuery)}`, { withCredentials: true });
      setSearchResults(mergeSearchResults(res.data));
    } catch { setSearchResults([]); }
    setSearching(false);
  };

  // Check duplicates for new vehicle
  const checkDuplicate = async (field, value) => {
    if (!value || value.length < 3) return;
    try {
      const params = field === "reg_no" ? `reg_no=${encodeURIComponent(value)}` : `vin=${encodeURIComponent(value)}`;
      const res = await axios.get(`${API}/reception/check-duplicate?${params}`, { withCredentials: true });
      setDupError(prev => ({ ...prev, [field === "reg_no" ? "reg" : "vin"]: field === "reg_no" ? res.data.duplicate_reg : res.data.duplicate_vin }));
    } catch {}
  };

  // Select existing vehicle → prefill contact data.
  // `overrideVehicle` lets callers pass the vehicle object directly, sidestepping
  // the React state-race where `searchResults` hasn't applied yet after a
  // `setSearchResults` call in the same event turn (used by the "arrived from
  // an appointment" auto-open path below).
  const selectVehicle = async (index, overrideVehicle = null) => {
    const v = overrideVehicle || searchResults[index];
    if (!v) return;
    
    setSelectedVehicleIndex(index);
    setCreateNew(false);
    // Track if VIN was originally missing
    const vinMissing = !v.vin;
    setSelectedVinMissing(vinMissing);
    setSelectedVin(v.vin || "");
    // Track if Reg No was originally missing
    const regNoMissing = !v.vehicle_reg_no;
    setSelectedRegNoMissing(regNoMissing);
    setSelectedRegNo(v.vehicle_reg_no || "");
    // Try to get full stored customer data
    try {
      const res = await axios.get(`${API}/reception/vehicle/${encodeURIComponent(v.vehicle_reg_no)}`, { withCredentials: true });
      if (res.data) {
        const d = res.data;
        const sal = d.salutation || (d.customer_type === "Company" ? "M/S" : "Mr");
        const names = resolveCustomerNames(d);
        setContactData({
          salutation: sal,
          first_name: names.first_name, last_name: names.last_name,
          company_name: d.company_name || "",
          address: d.contact_address || d.address || "",
          city: d.contact_city || d.city || "",
          state: d.contact_state || d.state || "",
          pin: d.contact_zipcode || d.pin || "",
          contact_no: d.customer_phone || d.contact_no || v.customer_phone || "",
          alternate_no: d.alternate_no || "", email: d.customer_email || d.email || v.customer_email || "",
          date_of_birth: d.date_of_birth || "", anniversary: d.anniversary || "",
          driven_by: d.driven_by || "Owner",
          preferred_contact_mode: d.preferred_contact_mode || [],
          preferred_contact_time: d.preferred_contact_time || [],
        });
        setCustomerType(sal === "M/S" ? "Company" : "Individual");
        // Store old data for masked display
        setOldContactData({ ...d });
        return;
      }
    } catch {}
    // Fallback: use search result data
    const sal = v.salutation || (v.customer_type === "Company" ? "M/S" : "Mr");
    const fallbackNames = resolveCustomerNames(v);
    setContactData(prev => ({
      ...prev,
      salutation: sal,
      first_name: fallbackNames.first_name,
      last_name: fallbackNames.last_name,
      company_name: v.company_name || "",
      contact_no: v.customer_phone || "", email: v.customer_email || "",
    }));
    setCustomerType(sal === "M/S" ? "Company" : "Individual");
    setOldContactData(null);
  };

  // Mask value for display
  const maskValue = (val) => {
    if (!val || val.length < 4) return "";
    return val[0] + val[1] + "X".repeat(Math.max(0, val.length - 4)) + val.slice(-2);
  };

  // Reset wizard
  const resetWizard = () => {
    setStep(1); setSearchQuery(""); setSearchResults([]); setSearched(false);
    setSelectedVehicleIndex(-1); setSelectedVin(""); setSelectedVinMissing(false); 
    setSelectedRegNo(""); setSelectedRegNoMissing(false); setCreateNew(false);
    setNewVehicle({ vehicle_reg_no: "", vin: "", engine_no: "" });
    setDupError({ reg: false, vin: false });
    setReceptionTime(""); setEntrySource("Walk-in");
    setIsFromAppointment(false);
    setCustomerType("Individual");
    setContactData({ salutation: "Mr", first_name: "", last_name: "", company_name: "", address: "", city: "", state: "", pin: "", contact_no: "", alternate_no: "", email: "", date_of_birth: "", anniversary: "", driven_by: "Owner", preferred_contact_mode: [], preferred_contact_time: [] });
    setStep2Errors({});
    setOldContactData(null);
    setDocs({ insurance_attached: false, insurance_not_collected: false, insurance_reason: "", rc_attached: false, rc_not_collected: false, rc_reason: "" });
    setDocFiles({ insurance: null, rc: null });
    setOtherData({ service_type: "", pms_interval: "", allocated_sa: "", current_km: "", specific_repair_request: "",
      ots_recall: false, priority_customer: false, recovered_lost_customer: false });
  };

  // Save entry
  const handleSave = async () => {
    // Synchronous ref, not state: two clicks in the same JS tick would both see
    // savingEntry === false, because setState is async.
    if (savingEntryRef.current) return;
    const regNo = createNew ? newVehicle.vehicle_reg_no : getSelectedRegNo();
    const vin = createNew ? newVehicle.vin : getSelectedVin();
    if (!regNo || !vin) { toast.error("Vehicle Reg No and VIN are required"); return; }
    if (!receptionTime) { toast.error("Vehicle Reception Time is required"); return; }
    if (!contactData.salutation) { toast.error("Salutation is required"); return; }
    const isCompany = contactData.salutation === "M/S";
    if (!isCompany && (!contactData.first_name || !contactData.last_name)) { toast.error("First Name and Last Name are required"); return; }
    if (isCompany && !contactData.company_name) { toast.error("Company Name is required"); return; }
    if (!contactData.contact_no) { toast.error("Contact No is required"); return; }

    savingEntryRef.current = true;
    setSavingEntry(true);
    try {
      await axios.post(`${API}/reception`, {
        vehicle_reception_time: localToUtcISO(receptionTime),
        source: entrySource,
        vehicle_reg_no: regNo,
        vin: vin,
        engine_no: createNew ? newVehicle.engine_no : (selectedVehicle?.engine_no || ""),
        ...contactData,
        insurance_attached: docs.insurance_attached,
        insurance_not_collected: docs.insurance_not_collected,
        insurance_not_collected_reason: docs.insurance_reason,
        rc_attached: docs.rc_attached,
        rc_not_collected: docs.rc_not_collected,
        rc_not_collected_reason: docs.rc_reason,
        service_type: otherData.service_type === "PMS" ? otherData.pms_interval : otherData.service_type,
        allocated_sa: otherData.allocated_sa,
        current_km: Number(otherData.current_km),
        specific_repair_request: otherData.specific_repair_request,
        ots_recall: otherData.ots_recall,
        priority_customer: otherData.priority_customer,
        recovered_lost_customer: otherData.recovered_lost_customer,
        branch: branches[0]?.name || "",
        linked_appointment_id: selectedVehicle?.appointment_id || "",
      }, { withCredentials: true });
      // Reception upserts the vehicle, so it exists now. Look up its id and
      // send any files the receptionist picked in Step 3.
      if (docFiles.insurance || docFiles.rc) {
        try {
          const vres = await axios.get(`${API}/reception/vehicle/${encodeURIComponent(regNo)}`, { withCredentials: true });
          const vehId = vres.data?.vehicle_id;
          if (vehId) {
            const send = async (f, type) => {
              const fd = new FormData();
              fd.append("file", f);
              await axios.post(`${API}/vehicles/${vehId}/documents?document_type=${type}`, fd, { withCredentials: true });
            };
            if (docFiles.insurance) await send(docFiles.insurance, "insurance");
            if (docFiles.rc) await send(docFiles.rc, "rc");
          } else {
            toast.error("Entry saved, but the vehicle could not be found to attach documents");
          }
        } catch (err) {
          toast.error("Entry saved, but document upload failed: " + (err.response?.data?.detail || "unknown error"));
        }
      }
      toast.success("Reception entry created");
      setWizardOpen(false);
      resetWizard();
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create entry");
    } finally {
      savingEntryRef.current = false;
      setSavingEntry(false);
    }
  };

  // Delete reception entry
  const handleDelete = async (entryId) => {
    if (deletingEntryRef.current) return;
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    deletingEntryRef.current = true;
    setDeletingEntry(true);
    try {
      await axios.delete(`${API}/reception/${entryId}`, { withCredentials: true });
      toast.success("Entry deleted");
      fetchEntries();
      if (detailOpen && detailEntry?.entry_id === entryId) {
        setDetailOpen(false);
        setDetailEntry(null);
        navigate("/customer-relations/reception");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete entry");
    } finally {
      deletingEntryRef.current = false;
      setDeletingEntry(false);
    }
  };

  // The datetime-local inputs carry the browser's wall clock. Convert to a real
  // UTC instant on the way out so vehicle_reception_time and entry_time share
  // one clock.
  const localToUtcISO = (v) => {
    if (!v) return v;
    const d = new Date(v);
    return isNaN(d) ? v : d.toISOString();
  };

  // Step 2 fields are validated on the step they live on, not at save time.
  const validateStep2 = () => {
    const isCompany = contactData.salutation === "M/S";
    const errs = {};
    if (!contactData.salutation) errs.salutation = "Salutation is required";
    if (isCompany) {
      if (!contactData.company_name?.trim()) errs.company_name = "Company Name is required";
    } else {
      if (!contactData.first_name?.trim()) errs.first_name = "First Name is required";
      if (!contactData.last_name?.trim()) errs.last_name = "Last Name is required";
    }
    if (!contactData.contact_no?.trim()) errs.contact_no = "Contact No is required";
    setStep2Errors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleWizardNext = () => {
    if (step === 2 && !validateStep2()) return;
    setStep((s) => s + 1);
  };

  const fmtTime = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      const date = formatDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`);
      const time = formatTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
      return <span className="whitespace-nowrap">{date}<br />{time}</span>;
    } catch { return iso; }
  };

  const canGoNext3 = !!(otherData.service_type && (otherData.service_type !== "PMS" || otherData.pms_interval) && otherData.allocated_sa
    && String(otherData.current_km).trim() !== "" && Number(otherData.current_km) >= 0
    && otherData.specific_repair_request.trim());
  const canGoNext1 = selectedVehicle ? (getSelectedRegNo() && getSelectedVin()) : (createNew && newVehicle.vehicle_reg_no && newVehicle.vin && !dupError.reg && !dupError.vin);

  // Helpers to get final reg/vin accounting for inline edits on selected vehicle
  function getSelectedRegNo() { return selectedRegNoMissing ? selectedRegNo : (selectedVehicle?.vehicle_reg_no || ""); }
  function getSelectedVin() { return selectedVinMissing ? selectedVin : (selectedVehicle?.vin || ""); }

  // ===================== EDIT ENTRY =====================
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editDiscardOpen, setEditDiscardOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const editSnapshotRef = useRef(null);

  // <input type="datetime-local"> requires "YYYY-MM-DDTHH:MM" and refuses
  // any timezone/microsecond suffix. Backend stores gate_out_time as full
  // ISO ("...+00:00") — slice down to the 16-char naive-local form.
  const toLocalDT = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d)) return String(v).slice(0, 16);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  };

  const openEdit = (entry) => {
    const d = {
      vehicle_reception_time: toLocalDT(entry.vehicle_reception_time),
      gate_out_time: toLocalDT(entry.gate_out_time),
      gatepass_number: entry.gatepass_number || "",
      source: entry.source || "Walk-in",
      salutation: entry.salutation || "Mr",
      first_name: entry.first_name || "",
      last_name: entry.last_name || "",
      company_name: entry.company_name || "",
      contact_no: entry.contact_no || "",
      alternate_no: entry.alternate_no || "",
      email: entry.email || "",
      address: entry.address || "",
      city: entry.city || "",
      state: entry.state || "",
      pin: entry.pin || "",
      date_of_birth: entry.date_of_birth || "",
      anniversary: entry.anniversary || "",
      driven_by: entry.driven_by || "",
      service_type: entry.service_type || "",
      allocated_sa: entry.allocated_sa || "",
      current_km: entry.current_km ?? "",
      specific_repair_request: entry.specific_repair_request || "",
      ots_recall: !!entry.ots_recall,
      priority_customer: !!entry.priority_customer,
      recovered_lost_customer: !!entry.recovered_lost_customer,
      preferred_contact_mode: entry.preferred_contact_mode || [],
      preferred_contact_time: entry.preferred_contact_time || [],
      insurance_attached: !!entry.insurance_attached,
      insurance_not_collected: !!entry.insurance_not_collected,
      insurance_not_collected_reason: entry.insurance_not_collected_reason || "",
      rc_attached: !!entry.rc_attached,
      rc_not_collected: !!entry.rc_not_collected,
      rc_not_collected_reason: entry.rc_not_collected_reason || "",
    };
    setEditData(d);
    editSnapshotRef.current = JSON.stringify(d);
    setEditOpen(true);
  };

  const updateEditField = (field, value) => setEditData((prev) => ({ ...prev, [field]: value }));

  const handleEditSave = async () => {
    const isCompany = editData.salutation === "M/S";
    if (!isCompany && (!editData.first_name || !editData.last_name)) { toast.error("First Name and Last Name are required"); return; }
    if (isCompany && !editData.company_name) { toast.error("Company Name is required"); return; }
    if (!editData.contact_no) { toast.error("Contact No is required"); return; }
    if (!editData.vehicle_reception_time) { toast.error("Vehicle Reception Time is required"); return; }
    setSavingEdit(true);
    try {
      // Drop master-only fields from the payload when the visible value
      // hasn't changed since the form opened — this avoids two problems:
      // (1) sending the truncated "YYYY-MM-DDTHH:MM" form of gate_out_time
      // back to the server and losing the seconds/timezone that were
      // originally stored, and (2) a non-master user triggering a 403 just
      // because the datetime input round-tripped its own value.
      const initial = editSnapshotRef.current ? JSON.parse(editSnapshotRef.current) : {};
      const payload = { ...editData };
      for (const f of ["vehicle_reception_time", "gate_out_time", "gatepass_number"]) {
        if ((payload[f] || "") === (initial[f] || "")) delete payload[f];
      }
      // Whatever survived came from a datetime-local input in the browser's
      // zone — send the UTC instant so storage stays on one clock.
      for (const f of ["vehicle_reception_time", "gate_out_time"]) {
        if (payload[f]) payload[f] = localToUtcISO(payload[f]);
      }
      const res = await axios.put(`${API}/reception/${detailEntry.entry_id}`, payload, { withCredentials: true });
      toast.success("Entry updated");
      setDetailEntry(res.data);
      setEditOpen(false);
      fetchEntries();
      // Refresh activity log for the newly-logged field changes
      await refetchActivity(res.data.entry_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update entry");
    } finally {
      setSavingEdit(false);
    }
  };

  // ===================== UPDATE MENU (Gate Out + Update Documents) =====================
  const openGateOut = () => { setGatepassNumber(""); setGateOutOpen(true); };
  const handleGateOut = async () => {
    if (!gatepassNumber.trim()) { toast.error("Gatepass number is required"); return; }
    setGateOutSaving(true);
    try {
      const res = await axios.post(
        `${API}/reception/${detailEntry.entry_id}/gate-out`,
        { gatepass_number: gatepassNumber.trim() },
        { withCredentials: true }
      );
      toast.success("Vehicle gated out");
      setDetailEntry(res.data);
      setGateOutOpen(false);
      fetchEntries();
      await refetchActivity(res.data.entry_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to gate out");
    } finally {
      setGateOutSaving(false);
    }
  };

  const openDocUpdate = () => {
    setDocUpdateData({
      insurance_attached: !!detailEntry.insurance_attached,
      insurance_not_collected: !!detailEntry.insurance_not_collected,
      insurance_not_collected_reason: detailEntry.insurance_not_collected_reason || "",
      rc_attached: !!detailEntry.rc_attached,
      rc_not_collected: !!detailEntry.rc_not_collected,
      rc_not_collected_reason: detailEntry.rc_not_collected_reason || "",
    });
    setDocUpdateOpen(true);
  };
  const updateDocField = (field, value) => setDocUpdateData((prev) => ({ ...prev, [field]: value }));
  const handleDocUpdate = async () => {
    setDocUpdateSaving(true);
    try {
      const res = await axios.post(
        `${API}/reception/${detailEntry.entry_id}/documents`,
        docUpdateData,
        { withCredentials: true }
      );
      toast.success("Documents updated");
      setDetailEntry(res.data);
      setDocUpdateOpen(false);
      fetchEntries();
      await refetchActivity(res.data.entry_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update documents");
    } finally {
      setDocUpdateSaving(false);
    }
  };

  // ===================== RENDER =====================

  // Per-column class + cell renderer for the main register (used by both
  // header alignment and body cells so column order stays consistent).
  const renderCellClass = (id) => {
    if (id === "actions") return "w-12";
    if (id === "vin") return "text-sm text-gray-500 whitespace-nowrap";
    return "text-sm whitespace-nowrap";
  };

  const renderReceptionCell = (id, e) => {
    switch (id) {
      case "gate_in": return fmtTime(e.vehicle_reception_time);
      case "gate_out":
        return e.gate_out_time ? fmtTime(e.gate_out_time) : <span className="text-gray-300">—</span>;
      case "source": return <Badge variant="outline" className="rounded-sm text-xs">{e.source}</Badge>;
      case "reg_no": return <span className="text-sm font-medium">{e.vehicle_reg_no}</span>;
      case "vin": return e.vin || "-";
      case "model": return e.model || "-";
      case "customer": return e.customer_name || "-";
      case "phone": return e.contact_no || "-";
      case "contact_valid":
        return (
          <div className="flex justify-center">
            {e.contact_validation
              ? <Badge className="bg-green-100 text-green-800 rounded-sm text-xs">Yes</Badge>
              : <Badge variant="outline" className="rounded-sm text-xs">No</Badge>}
          </div>
        );
      case "documents":
        return (
          <div className="flex gap-1 justify-center">
            {e.insurance_attached
              ? <Badge className="bg-green-100 text-green-800 rounded-sm text-[10px]">Ins</Badge>
              : <Badge variant="outline" className="rounded-sm text-[10px] text-red-500 border-red-200">Ins</Badge>}
            {e.rc_attached
              ? <Badge className="bg-green-100 text-green-800 rounded-sm text-[10px]">RC</Badge>
              : <Badge variant="outline" className="rounded-sm text-[10px] text-red-500 border-red-200">RC</Badge>}
          </div>
        );
      case "status":
        return (
          <Badge className={`rounded-sm text-xs ${e.status === "Completed" ? "bg-green-100 text-green-800" : e.status === "Documents Pending" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-800"}`}>
            {e.status}
          </Badge>
        );
      case "actions":
        return (
          <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm" onClick={() => { setDetailEntry(e); setDetailOpen(true); }} data-testid={`action-btn-${e.entry_id}`}>
            <Eye className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        );
      default: return null;
    }
  };

  // Detail view
  if (detailOpen && detailEntry) {
    return (
      <div className="space-y-6" data-testid="reception-detail">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => { setDetailOpen(false); setDetailEntry(null); navigate("/customer-relations/reception"); }} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <div>
              <h1 className="font-heading font-black text-2xl tracking-tight uppercase">{detailEntry.vehicle_reg_no}</h1>
              <p className="text-sm text-gray-500 ">{detailEntry.entry_id}</p>
            </div>
            <Badge className={`ml-2 rounded-sm text-xs ${detailEntry.status === "Completed" ? "bg-green-100 text-green-800" : detailEntry.status === "Documents Pending" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-800"}`}>{detailEntry.status}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <TooltipProvider>
              {canUpdateReception && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      data-testid="detail-update-btn"
                    >
                      Update <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-sm">
                    <DropdownMenuItem
                      onClick={openGateOut}
                      disabled={!!detailEntry.gate_out_time}
                      data-testid="update-gate-out-item"
                    >
                      <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      {detailEntry.gate_out_time ? "Gate Out (Done)" : "Gate Out"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openDocUpdate} data-testid="update-documents-item">
                      <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Update Documents
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {canEditReception && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 rounded-sm"
                      onClick={() => openEdit(detailEntry)}
                      data-testid="detail-edit-btn"
                      aria-label="Edit entry"
                    >
                      <Pencil className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
              )}
              {canMasterReception && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                      onClick={() => handleDelete(detailEntry.entry_id)}
                      disabled={deletingEntry}
                      data-testid="detail-delete-btn"
                      aria-label="Delete entry"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardHeader className="border-b border-gray-100 pb-4"><CardTitle className="font-heading font-bold text-base tracking-tight uppercase flex items-center gap-2">Vehicle</CardTitle></CardHeader>
              <CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div><Label className="form-label">Reg No</Label><p className="text-sm  mt-1">{detailEntry.vehicle_reg_no}</p></div>
                <div><Label className="form-label">VIN</Label><p className="text-sm  mt-1">{detailEntry.vin || "-"}</p></div>
                <div><Label className="form-label">Engine No</Label><p className="text-sm  mt-1">{detailEntry.engine_no || "-"}</p></div>
                <div><Label className="form-label">Model</Label><p className="text-sm  mt-1">{detailEntry.model || "-"}</p></div>
                <div><Label className="form-label">Source</Label><p className="text-sm mt-1">{detailEntry.source}</p></div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardHeader className="border-b border-gray-100 pb-4"><CardTitle className="font-heading font-bold text-base tracking-tight uppercase flex items-center gap-2">Service Details</CardTitle></CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div><Label className="form-label">Service Type</Label><p className="text-sm mt-1">{detailEntry.service_type || "-"}</p></div>
                  <div><Label className="form-label">Service Advisor</Label><p className="text-sm mt-1">{detailEntry.allocated_sa || "-"}</p></div>
                  <div><Label className="form-label">Current KM</Label><p className="text-sm mt-1">{detailEntry.current_km ?? "-"}</p></div>
                  <div><Label className="form-label">Flags</Label><p className="text-sm mt-1">
                    {[detailEntry.ots_recall && "OTS/Recall", detailEntry.priority_customer && "Priority", detailEntry.recovered_lost_customer && "Recovered"].filter(Boolean).join(", ") || "-"}
                  </p></div>
                </div>
                <div><Label className="form-label">Specific Repair Request</Label><p className="text-sm mt-1 whitespace-pre-wrap">{detailEntry.specific_repair_request || "-"}</p></div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardHeader className="border-b border-gray-100 pb-4"><CardTitle className="font-heading font-bold text-base tracking-tight uppercase flex items-center gap-2">Customer</CardTitle></CardHeader>
              <CardContent className="p-6 grid grid-cols-2 md:grid-cols-3 gap-6">
                <div><Label className="form-label">Name</Label><p className="text-sm mt-1">{detailEntry.customer_name || "-"}</p></div>
                <div><Label className="form-label">Phone</Label><p className="text-sm mt-1">{detailEntry.contact_no}</p></div>
                <div><Label className="form-label">Email</Label><p className="text-sm mt-1">{detailEntry.email || "-"}</p></div>
                <div><Label className="form-label">Address</Label><p className="text-sm mt-1">{[detailEntry.address, detailEntry.city, detailEntry.state, detailEntry.pin].filter(Boolean).join(", ") || "-"}</p></div>
                <div><Label className="form-label">Driven By</Label><p className="text-sm mt-1">{detailEntry.driven_by || "-"}</p></div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardHeader className="border-b border-gray-100 pb-4"><CardTitle className="font-heading font-bold text-base tracking-tight uppercase">Timings</CardTitle></CardHeader>
              <CardContent className="p-6 space-y-4">
                <div><Label className="form-label">Gate In</Label><p className="text-sm  mt-1" data-testid="detail-gate-in">{fmtTime(detailEntry.vehicle_reception_time)}</p></div>
                <div>
                  <Label className="form-label">Gate Out</Label>
                  <p className="text-sm mt-1" data-testid="detail-gate-out">
                    {detailEntry.gate_out_time ? (
                      <>
                        {fmtTime(detailEntry.gate_out_time)}
                        {detailEntry.gatepass_number && (
                          <span className="block text-[10px] text-gray-500 mt-0.5">
                            Gatepass: {detailEntry.gatepass_number}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 rounded-sm shadow-none">
              <CardHeader className="border-b border-gray-100 pb-4"><CardTitle className="font-heading font-bold text-base tracking-tight uppercase">Documents</CardTitle></CardHeader>
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Insurance</span>
                  {detailEntry.insurance_attached ? <Badge className="bg-green-100 text-green-800 rounded-sm text-xs">Attached</Badge> : detailEntry.insurance_not_collected ? <Badge variant="outline" className="rounded-sm text-xs">Not Collected</Badge> : <Badge variant="outline" className="rounded-sm text-xs text-red-600 border-red-200">Missing</Badge>}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">RC</span>
                  {detailEntry.rc_attached ? <Badge className="bg-green-100 text-green-800 rounded-sm text-xs">Attached</Badge> : detailEntry.rc_not_collected ? <Badge variant="outline" className="rounded-sm text-xs">Not Collected</Badge> : <Badge variant="outline" className="rounded-sm text-xs text-red-600 border-red-200">Missing</Badge>}
                </div>
              </CardContent>
            </Card>

            {/* Activity Log — reception scoped, newest first */}
            <Card className="border border-gray-200 rounded-sm shadow-none" data-testid="detail-activity-card">
              <CardHeader className="border-b border-gray-100 pb-4">
                <CardTitle className="font-heading font-bold text-base tracking-tight uppercase flex items-center gap-2">
                  <History className="w-4 h-4" strokeWidth={1.5} /> Activity
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {activityLogs.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 text-sm" data-testid="activity-empty">No activity yet</div>
                ) : (
                  <div className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
                    {activityLogs.map((log) => (
                      <div key={log.log_id} className="p-4" data-testid={`activity-log-${log.log_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold">{log.user_name}</p>
                            <p className="text-xs text-gray-500">{log.action}</p>
                            {log.field_changed && (log.old_value || log.new_value) && (
                              <p className="text-xs text-gray-400 mt-1 break-all">
                                {log.old_value || "—"} → {log.new_value || "—"}
                              </p>
                            )}
                          </div>
                          <span className="text-[10px] text-gray-400 whitespace-nowrap">
                            {formatISTTimestamp(log.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Edit Entry Dialog — reception.edit only */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent
            className="rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto"
            data-testid="edit-entry-modal"
            onInteractOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
            closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
            onCloseClick={(e) => {
              if (JSON.stringify(editData) !== editSnapshotRef.current) {
                e.preventDefault();
                setEditDiscardOpen(true);
              }
            }}
          >
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-lg tracking-tight uppercase">Edit Entry — {detailEntry.vehicle_reg_no}</DialogTitle>
            </DialogHeader>
            {editData && (
              <div className="space-y-5">
                {/* Vehicle identity + linked appointment: read-only. Wrong vehicle = delete and redo. */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-3 bg-gray-50 rounded-sm border border-gray-200">
                  <div><Label className="form-label">Reg No</Label><p className="text-sm font-bold mt-1">{detailEntry.vehicle_reg_no}</p></div>
                  <div><Label className="form-label">VIN</Label><p className="text-sm mt-1">{detailEntry.vin || "-"}</p></div>
                  <div><Label className="form-label">Engine No</Label><p className="text-sm mt-1">{detailEntry.engine_no || "-"}</p></div>
                  <div><Label className="form-label">Linked Appt</Label><p className="text-sm mt-1">{detailEntry.linked_appointment_id || "-"}</p></div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Gate In *</Label>
                    <Input
                      type="datetime-local"
                      value={editData.vehicle_reception_time}
                      onChange={(e) => updateEditField("vehicle_reception_time", e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-reception-time"
                      disabled={!canMasterReception}
                      readOnly={!canMasterReception}
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      {canMasterReception
                        ? "Correct a wrong stamp here — only Master can change this"
                        : "Gate In time can only be edited by Master"}
                    </p>
                  </div>
                  <div>
                    <Label className="form-label">Gate Out</Label>
                    <Input
                      type="datetime-local"
                      value={editData.gate_out_time || ""}
                      onChange={(e) => updateEditField("gate_out_time", e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-gate-out-time"
                      disabled={!canMasterReception}
                      readOnly={!canMasterReception}
                      placeholder="—"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      {canMasterReception
                        ? "Master-only. Blank until Gate Out is stamped from Update."
                        : "Gate Out time can only be edited by Master"}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Gatepass Number</Label>
                    <Input
                      value={editData.gatepass_number || ""}
                      onChange={(e) => updateEditField("gatepass_number", e.target.value)}
                      className="rounded-sm"
                      data-testid="edit-gatepass-number"
                      disabled={!canMasterReception}
                      readOnly={!canMasterReception}
                      placeholder="—"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Master-only. Set from the Gate Out action.</p>
                  </div>
                  <div>
                    <Label className="form-label">Source</Label>
                    <Select value={editData.source} onValueChange={(v) => updateEditField("source", v)}>
                      <SelectTrigger className="rounded-sm" data-testid="edit-source"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <CustomerNameFields formData={editData} updateField={updateEditField} />

                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="form-label">Contact No *</Label><Input value={editData.contact_no} onChange={(e) => updateEditField("contact_no", e.target.value)} className="rounded-sm" data-testid="edit-contact-no" /></div>
                  <div><Label className="form-label">Alternate No</Label><Input value={editData.alternate_no} onChange={(e) => updateEditField("alternate_no", e.target.value)} className="rounded-sm" data-testid="edit-alt-no" /></div>
                </div>
                <div><Label className="form-label">Email</Label><Input value={editData.email} onChange={(e) => updateEditField("email", e.target.value)} className="rounded-sm" data-testid="edit-email" /></div>
                <div><Label className="form-label">Address</Label><Input value={editData.address} onChange={(e) => updateEditField("address", e.target.value)} className="rounded-sm" data-testid="edit-address" /></div>
                <div className="grid grid-cols-3 gap-4">
                  <div><Label className="form-label">City</Label><Input value={editData.city} onChange={(e) => updateEditField("city", e.target.value)} className="rounded-sm" data-testid="edit-city" /></div>
                  <div><Label className="form-label">State</Label><Input value={editData.state} onChange={(e) => updateEditField("state", e.target.value)} className="rounded-sm" data-testid="edit-state" /></div>
                  <div><Label className="form-label">Pin</Label><Input value={editData.pin} onChange={(e) => updateEditField("pin", e.target.value)} className="rounded-sm" data-testid="edit-pin" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label className="form-label">Date of Birth</Label><Input type="date" value={editData.date_of_birth} onChange={(e) => updateEditField("date_of_birth", e.target.value)} className="rounded-sm" data-testid="edit-dob" /></div>
                  <div><Label className="form-label">Anniversary</Label><Input type="date" value={editData.anniversary} onChange={(e) => updateEditField("anniversary", e.target.value)} className="rounded-sm" data-testid="edit-anniversary" /></div>
                </div>
                <div>
                  <Label className="form-label">Driven By</Label>
                  <RadioGroup value={editData.driven_by} onValueChange={(v) => updateEditField("driven_by", v)} className="flex gap-4 mt-1">
                    {["Owner", "User", "Driver"].map(o => (
                      <div key={o} className="flex items-center gap-1.5"><RadioGroupItem value={o} id={`edit-driven-${o}`} /><Label htmlFor={`edit-driven-${o}`} className="text-sm">{o}</Label></div>
                    ))}
                  </RadioGroup>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Service Type</Label>
                    <Select value={editData.service_type} onValueChange={v => updateEditField("service_type", v)}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="edit-service-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                      <SelectContent>
                        {(settings?.service_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        {PMS_INTERVALS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">Service Advisor</Label>
                    <Select value={editData.allocated_sa} onValueChange={v => updateEditField("allocated_sa", v)}>
                      <SelectTrigger className="rounded-sm mt-1" data-testid="edit-advisor"><SelectValue placeholder="Assign SA" /></SelectTrigger>
                      <SelectContent>
                        {(settings?.service_advisors || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="form-label">Current KM</Label>
                  <Input type="number" min="0" className="rounded-sm mt-1" value={editData.current_km}
                    onChange={e => updateEditField("current_km", e.target.value)} data-testid="edit-current-km" />
                </div>
                <div>
                  <Label className="form-label">Specific Repair Request</Label>
                  <Textarea className="rounded-sm mt-1" rows={3} value={editData.specific_repair_request}
                    onChange={e => updateEditField("specific_repair_request", e.target.value)} data-testid="edit-repair-request" />
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {[["ots_recall", "OTS/Recall"], ["priority_customer", "Priority"], ["recovered_lost_customer", "Recovered"]].map(([k, t]) => (
                    <div key={k} className="flex items-center justify-between p-3 border border-gray-200 rounded-sm">
                      <p className="text-sm font-medium">{t}</p>
                      <Switch checked={!!editData[k]} onCheckedChange={v => updateEditField(k, v)} data-testid={`edit-switch-${k}`} />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">Preferred Contact Mode</Label>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {["Phone", "SMS", "Email"].map(m => (
                        <label key={m} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <Checkbox checked={editData.preferred_contact_mode.includes(m)} onCheckedChange={c => updateEditField("preferred_contact_mode", c ? [...editData.preferred_contact_mode, m] : editData.preferred_contact_mode.filter(x => x !== m))} />
                          {m}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="form-label">Preferred Contact Time</Label>
                    <div className="flex flex-wrap gap-3 mt-1">
                      {["Morning", "Afternoon", "Evening"].map(t => (
                        <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <Checkbox checked={editData.preferred_contact_time.includes(t)} onCheckedChange={c => updateEditField("preferred_contact_time", c ? [...editData.preferred_contact_time, t] : editData.preferred_contact_time.filter(x => x !== t))} />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Documents */}
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { key: "insurance", label: "Insurance Document" },
                    { key: "rc", label: "Registration (RC) Document" },
                  ].map(({ key, label }) => (
                    <div key={key} className="p-4 border border-gray-200 rounded-sm space-y-3">
                      <p className="text-sm font-bold">{label}</p>
                      <Button
                        variant={editData[`${key}_attached`] ? "default" : "outline"}
                        size="sm"
                        className="rounded-sm"
                        onClick={() => updateEditField(`${key}_attached`, !editData[`${key}_attached`])}
                        data-testid={`edit-${key}-attach-btn`}
                      >
                        {editData[`${key}_attached`] ? <><FileCheck className="w-4 h-4 mr-1" /> Attached</> : <><Paperclip className="w-4 h-4 mr-1" /> Attach</>}
                      </Button>
                      <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox
                          checked={editData[`${key}_not_collected`]}
                          onCheckedChange={(c) => updateEditField(`${key}_not_collected`, !!c)}
                          data-testid={`edit-${key}-not-collected`}
                        />
                        Not collected
                      </label>
                      {editData[`${key}_not_collected`] && (
                        <Input
                          placeholder="Reason not collected"
                          value={editData[`${key}_not_collected_reason`]}
                          onChange={(e) => updateEditField(`${key}_not_collected_reason`, e.target.value)}
                          className="rounded-sm"
                          data-testid={`edit-${key}-reason`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button size="sm" className="rounded-sm bg-black text-white hover:bg-gray-800" onClick={handleEditSave} disabled={savingEdit} data-testid="edit-save-btn">
                {savingEdit ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <DiscardConfirm
          open={editDiscardOpen}
          onOpenChange={setEditDiscardOpen}
          onDiscard={() => {
            setEditDiscardOpen(false);
            setEditOpen(false);
          }}
        />

        {/* Gate Out Dialog */}
        <Dialog open={gateOutOpen} onOpenChange={setGateOutOpen}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="gate-out-modal">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-lg tracking-tight uppercase">
                Gate Out — {detailEntry.vehicle_reg_no}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-xs text-gray-500">
                Stamp the vehicle as gated out. Gate Out time is set to the current moment on save.
              </p>
              <div>
                <Label className="form-label">Gatepass Number *</Label>
                <Input
                  value={gatepassNumber}
                  onChange={(e) => setGatepassNumber(e.target.value)}
                  placeholder="e.g. GP-2026-001"
                  className="rounded-sm uppercase"
                  data-testid="gate-out-gatepass-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setGateOutOpen(false)} data-testid="gate-out-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                className="rounded-sm bg-black text-white hover:bg-gray-800"
                onClick={handleGateOut}
                disabled={gateOutSaving || !gatepassNumber.trim()}
                data-testid="gate-out-save-btn"
              >
                {gateOutSaving ? "Saving..." : "Confirm Gate Out"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Documents Dialog */}
        <Dialog open={docUpdateOpen} onOpenChange={setDocUpdateOpen}>
          <DialogContent className="rounded-sm sm:max-w-lg" data-testid="update-documents-modal">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-lg tracking-tight uppercase">
                Update Documents — {detailEntry.vehicle_reg_no}
              </DialogTitle>
            </DialogHeader>
            {docUpdateData && (
              <div className="grid md:grid-cols-2 gap-4 py-2">
                {[
                  { key: "insurance", label: "Insurance Document" },
                  { key: "rc", label: "Registration (RC) Document" },
                ].map(({ key, label }) => (
                  <div key={key} className="p-4 border border-gray-200 rounded-sm space-y-3">
                    <p className="text-sm font-bold">{label}</p>
                    <Button
                      variant={docUpdateData[`${key}_attached`] ? "default" : "outline"}
                      size="sm"
                      className="rounded-sm"
                      onClick={() => updateDocField(`${key}_attached`, !docUpdateData[`${key}_attached`])}
                      data-testid={`doc-update-${key}-attach-btn`}
                    >
                      {docUpdateData[`${key}_attached`]
                        ? <><FileCheck className="w-4 h-4 mr-1" /> Attached</>
                        : <><Paperclip className="w-4 h-4 mr-1" /> Attach</>}
                    </Button>
                    <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox
                        checked={docUpdateData[`${key}_not_collected`]}
                        onCheckedChange={(c) => updateDocField(`${key}_not_collected`, !!c)}
                        data-testid={`doc-update-${key}-not-collected`}
                      />
                      Not collected
                    </label>
                    {docUpdateData[`${key}_not_collected`] && (
                      <Input
                        placeholder="Reason not collected"
                        value={docUpdateData[`${key}_not_collected_reason`]}
                        onChange={(e) => updateDocField(`${key}_not_collected_reason`, e.target.value)}
                        className="rounded-sm"
                        data-testid={`doc-update-${key}-reason`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setDocUpdateOpen(false)} data-testid="doc-update-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                className="rounded-sm bg-black text-white hover:bg-gray-800"
                onClick={handleDocUpdate}
                disabled={docUpdateSaving}
                data-testid="doc-update-save-btn"
              >
                {docUpdateSaving ? "Saving..." : "Save Documents"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  const renderEntriesTable = (rows, isLoading, emptyText) => (
    <Card className="border border-gray-200 rounded-sm shadow-none overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-gray-50/80">
            {visibleCols.map((col) => (
              <TableHead
                key={col.id}
                className={`text-[10px] uppercase tracking-widest font-bold text-gray-500 whitespace-nowrap text-center${col.id === "actions" ? " w-12" : ""}`}
              >
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow><TableCell colSpan={visibleCols.length} className="text-center py-12 text-gray-400">Loading...</TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow><TableCell colSpan={visibleCols.length} className="text-center py-12 text-gray-400">{emptyText}</TableCell></TableRow>
          ) : rows.map((e) => (
            <TableRow key={e.entry_id} className="hover:bg-gray-50/50" data-testid={`row-${e.entry_id}`}>
              {visibleCols.map((col) => (
                <TableCell key={col.id} className={renderCellClass(col.id)}>
                  {renderReceptionCell(col.id, e)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );

  // Main register view
  return (
    <div className="space-y-6" data-testid="reception-page">
      {/* Standard Header */}
      <PageHeader
        icon={ClipboardList}
        title="Reception"
        subtitle="Vehicle reception register"
        onFilter={() => setFilterOpen(true)}
        filterTestId="reception-filter-btn"
        onRefresh={() => { fetchEntries(); fetchWorkshop(); }}
        primaryAction={canUpdateReception ? {
          label: "New Entry",
          onClick: () => {
            resetWizard();
            // Reception time = the true moment the vehicle entered the workshop.
            // Auto-stamped for BOTH paths; never typed by hand.
            const now = new Date();
            const tzOffsetMs = now.getTimezoneOffset() * 60000;
            setReceptionTime(new Date(now - tzOffsetMs).toISOString().slice(0, 16));
            setWizardOpen(true);
          },
          icon: Plus,
          testId: "new-entry-btn",
        } : null}
        columnControls={{
          onCustomize: columnManager.openCustomize,
          onRearrange: () => columnManager.openRearrange(),
        }}
        leftSlot={
          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 rounded-sm p-1 w-44">
              {[{ id: "current", label: "CURRENT" }, { id: "history", label: "HISTORY" }].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex-1 py-2 text-xs uppercase tracking-wider rounded-sm transition-colors ${activeTab === t.id ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-black"}`}
                  data-testid={`reception-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className={`flex bg-gray-100 rounded-sm p-1 w-44${activeTab === "history" ? "" : " invisible"}`}>
              {[{ id: "entry_time", label: "GATE IN" }, { id: "gate_out_time", label: "GATE OUT" }].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setHistoryDateField(t.id)}
                  className={`flex-1 py-2 text-xs uppercase tracking-wider rounded-sm transition-colors ${historyDateField === t.id ? "bg-white text-black shadow-sm" : "text-gray-500 hover:text-black"}`}
                  data-testid={`reception-datefield-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className={`flex bg-gray-100 rounded-sm p-1 w-80${activeTab === "history" ? "" : " invisible"}`}>
              {[{ id: "date", label: "Date" }, { id: "month", label: "Month" }, { id: "year", label: "Year" }, { id: "custom", label: "Custom" }].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setDateMode(m.id)}
                  className={`flex-1 py-2 text-xs uppercase tracking-wider rounded-sm transition-colors ${dateMode === m.id ? "bg-white text-black shadow-sm font-medium" : "text-gray-500 hover:text-black"}`}
                  data-testid={`reception-datemode-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {activeTab === "history" && (
        <div className="flex items-center justify-center gap-2">
          {dateMode === "custom" ? (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="min-w-[200px] rounded-sm text-sm" data-testid="reception-custom-from">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customFrom ? fmtLabel(new Date(`${customFrom}T00:00:00`), "date") : "From date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                  <Calendar mode="single" selected={customFrom ? new Date(`${customFrom}T00:00:00`) : undefined} onSelect={(d) => d && setCustomFrom(toISODate(d))} initialFocus />
                </PopoverContent>
              </Popover>
              <span className="text-gray-400 text-sm px-1">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="min-w-[200px] rounded-sm text-sm" data-testid="reception-custom-to">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customTo ? fmtLabel(new Date(`${customTo}T00:00:00`), "date") : "To date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                  <Calendar mode="single" selected={customTo ? new Date(`${customTo}T00:00:00`) : undefined} onSelect={(d) => d && setCustomTo(toISODate(d))} initialFocus />
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <>
              <Button variant="ghost" size="icon" onClick={() => stepAnchor(-1)} className="h-10 w-10 rounded-sm" data-testid="reception-date-prev">
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </Button>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="min-w-[200px] rounded-sm text-sm" data-testid="reception-date-anchor">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {anchorLabel()}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                  <Calendar mode="single" selected={anchorObj} onSelect={(d) => d && setAnchorDate(toISODate(d))} initialFocus />
                </PopoverContent>
              </Popover>
              <Button variant="ghost" size="icon" onClick={() => stepAnchor(1)} className="h-10 w-10 rounded-sm" data-testid="reception-date-next">
                <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </Button>
            </>
          )}
        </div>
      )}

      {activeTab === "current" ? (
        <>
          {renderEntriesTable(entries, loading, "No entries today")}
          <div>
            <h2 className="text-[11px] uppercase tracking-widest font-bold text-gray-500 mb-2">In Workshop</h2>
            {renderEntriesTable(workshopEntries, workshopLoading, "No vehicles in workshop")}
          </div>
        </>
      ) : (
        renderEntriesTable(entries, loading, "No entries found")
      )}

      {/* ========== 3-STEP WIZARD MODAL ========== */}
      <Dialog open={wizardOpen} onOpenChange={(open) => { if (!open) { setWizardOpen(false); resetWizard(); } }}>
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="wizard-modal"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (step > 1 || searchQuery !== "") {
              e.preventDefault();
              setDiscardConfirmOpen(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-lg tracking-tight uppercase">
              New Reception Entry — Step {step} of 4
            </DialogTitle>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4].map(s => (
                <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-black" : "bg-gray-200"}`} />
              ))}
            </div>
          </DialogHeader>

          {/* STEP 1: Find Vehicle */}
          {step === 1 && (
            <div className="space-y-5 py-2" data-testid="wizard-step-1">
              <div className="flex gap-2">
                <Input placeholder="Enter Reg No / Phone / VIN" value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSearch()}
                  className="rounded-sm flex-1" data-testid="vehicle-search-input" />
                <Button onClick={handleSearch} className="rounded-sm bg-black text-white hover:bg-gray-800" disabled={searching} data-testid="vehicle-search-btn">
                  <Search className="w-4 h-4 mr-1" /> Search
                </Button>
              </div>

              {searched && !searching && searchResults.length === 0 && !createNew && (
                <div className="text-center py-4 border border-dashed border-gray-300 rounded-sm">
                  <p className="text-sm text-gray-500 mb-2">No vehicle found.</p>
                  <Button variant="outline" className="rounded-sm" onClick={() => setCreateNew(true)} data-testid="create-new-vehicle-btn">Create New Vehicle</Button>
                </div>
              )}

              {searchResults.length > 0 && !createNew && (
                <div className="space-y-2">
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {searchResults.map((r, i) => (
                      <div key={i} className={`p-3 border rounded-sm cursor-pointer transition-colors ${selectedVehicleIndex === i ? "border-black bg-gray-50" : "border-gray-200 hover:border-gray-400"}`}
                        onClick={() => {
                          // Only select if not already selected (prevents re-triggering)
                          if (selectedVehicleIndex !== i) {
                            selectVehicle(i);
                          }
                        }} data-testid={`search-result-${i}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm  font-bold">{r.vehicle_reg_no || "No Reg"}</p>
                            <p className="text-xs text-gray-500">{r.customer_name || `${r.first_name || ""} ${r.last_name || ""}`.trim()} &middot; {r.customer_phone}</p>
                          </div>
                          {r.model && <Badge variant="outline" className="rounded-sm text-xs">{r.model}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {selectedVehicle && selectedVinMissing && (
                    <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm space-y-2">
                      <p className="text-xs text-amber-700 font-medium">VIN is missing for this vehicle. Please enter:</p>
                      <Input placeholder="Enter VIN" className="rounded-sm uppercase" value={selectedVin}
                        onChange={e => setSelectedVin(e.target.value.toUpperCase().replace(/\s/g, ""))}
                        onKeyDown={e => {
                          // Prevent Enter from triggering search
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        data-testid="missing-vin-input" />
                    </div>
                  )}
                  {selectedVehicle && selectedRegNoMissing && (
                    <div className="p-3 border border-amber-300 bg-amber-50 rounded-sm space-y-2">
                      <p className="text-xs text-amber-700 font-medium">Registration No is missing. Please enter:</p>
                      <Input placeholder="Enter Reg No" className="rounded-sm uppercase" value={selectedRegNo}
                        onChange={e => setSelectedRegNo(e.target.value.toUpperCase().replace(/\s/g, ""))}
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                          }
                        }}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                        data-testid="missing-reg-input" />
                    </div>
                  )}
                  <Button variant="link" className="text-xs p-0 h-auto" onClick={() => setCreateNew(true)}>Or create new vehicle</Button>
                </div>
              )}

              {createNew && (
                <div className="space-y-3 p-4 border border-gray-200 rounded-sm bg-gray-50/50">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500">New Vehicle</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Registration No *</Label>
                      <Input value={newVehicle.vehicle_reg_no} className="rounded-sm uppercase"
                        onChange={e => setNewVehicle(v => ({ ...v, vehicle_reg_no: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                        onBlur={() => checkDuplicate("reg_no", newVehicle.vehicle_reg_no)}
                        data-testid="new-reg-no" />
                      {dupError.reg && <p className="text-xs text-red-600 mt-1" data-testid="dup-reg-error">Vehicle Already Exist</p>}
                    </div>
                    <div>
                      <Label className="text-xs">VIN *</Label>
                      <Input value={newVehicle.vin} className="rounded-sm uppercase"
                        onChange={e => setNewVehicle(v => ({ ...v, vin: e.target.value.toUpperCase().replace(/\s/g, "") }))}
                        onBlur={() => checkDuplicate("vin", newVehicle.vin)}
                        data-testid="new-vin" />
                      {dupError.vin && <p className="text-xs text-red-600 mt-1" data-testid="dup-vin-error">Vehicle Already Exist</p>}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Engine No (optional)</Label>
                    <Input value={newVehicle.engine_no} className="rounded-sm uppercase"
                      onChange={e => setNewVehicle(v => ({ ...v, engine_no: e.target.value.toUpperCase() }))}
                      data-testid="new-engine-no" />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Validate Contact */}
          {step === 2 && (
            <div className="space-y-4 py-2" data-testid="wizard-step-2">
              {/* Reception Time & Source - moved from Step 1 */}
              {/* Reception time is auto-stamped and locked on BOTH paths (true
                  workshop-entry moment). Source stays editable for walk-ins and
                  is locked only on the appointment hand-off path. */}
              <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-sm border border-gray-200">
                <div>
                  <Label className="form-label">Vehicle Reception Time *</Label>
                  <Input
                    type="datetime-local"
                    value={receptionTime}
                    disabled
                    readOnly
                    className="rounded-sm bg-white cursor-not-allowed opacity-70"
                    data-testid="reception-time"
                  />
                  <p className="text-[10px] text-gray-500 mt-1" data-testid="reception-time-locked-hint">
                    {isFromAppointment ? "Auto-stamped from appointment hand-off" : "Auto-stamped when the entry was started"}
                  </p>
                </div>
                <div>
                  <Label className="form-label">Source</Label>
                  <Select value={entrySource} onValueChange={setEntrySource} disabled={isFromAppointment}>
                    <SelectTrigger
                      className={`rounded-sm bg-white ${isFromAppointment ? "cursor-not-allowed opacity-70" : ""}`}
                      data-testid="entry-source"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {isFromAppointment && (
                    <p className="text-[10px] text-gray-500 mt-1" data-testid="entry-source-locked-hint">Locked to Appointment</p>
                  )}
                </div>
              </div>

              <CustomerNameFields
                formData={contactData}
                errors={step2Errors}
                updateField={(f, v) => {
                  setContactData((d) => ({ ...d, [f]: v }));
                  setStep2Errors((e) => ({ ...e, [f]: undefined }));
                  if (f === "salutation") {
                    setCustomerType(v === "M/S" ? "Company" : "Individual");
                  }
                }}
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldWithOld label="Contact No *" value={contactData.contact_no} oldValue={oldContactData?.contact_no} onChange={v => { setContactData(d => ({ ...d, contact_no: v })); setStep2Errors(e => ({ ...e, contact_no: undefined })); }} testId="contact-no" mask={maskValue} />
                  {step2Errors.contact_no && (
                    <p className="text-xs text-red-600 mt-1" data-testid="error-contact-no">{step2Errors.contact_no}</p>
                  )}
                </div>
                <FieldWithOld label="Alternate No" value={contactData.alternate_no} oldValue={oldContactData?.alternate_no} onChange={v => setContactData(d => ({ ...d, alternate_no: v }))} testId="alt-no" mask={maskValue} />
              </div>
              <FieldWithOld label="Email" value={contactData.email} oldValue={oldContactData?.email} onChange={v => setContactData(d => ({ ...d, email: v }))} testId="email" mask={maskValue} />

              <FieldWithOld label="Address" value={contactData.address} oldValue={oldContactData?.address} onChange={v => setContactData(d => ({ ...d, address: v }))} testId="address" mask={maskValue} />

              <div className="grid grid-cols-3 gap-3">
                <FieldWithOld label="City" value={contactData.city} oldValue={oldContactData?.city} onChange={v => setContactData(d => ({ ...d, city: v }))} testId="city" mask={maskValue} />
                <FieldWithOld label="State" value={contactData.state} oldValue={oldContactData?.state} onChange={v => setContactData(d => ({ ...d, state: v }))} testId="state" mask={maskValue} />
                <FieldWithOld label="Pin" value={contactData.pin} oldValue={oldContactData?.pin} onChange={v => setContactData(d => ({ ...d, pin: v }))} testId="pin" mask={maskValue} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Date of Birth</Label><Input type="date" value={contactData.date_of_birth} onChange={e => setContactData(d => ({ ...d, date_of_birth: e.target.value }))} className="rounded-sm" /></div>
                <div><Label className="text-xs">Anniversary</Label><Input type="date" value={contactData.anniversary} onChange={e => setContactData(d => ({ ...d, anniversary: e.target.value }))} className="rounded-sm" /></div>
              </div>

              <div>
                <Label className="form-label">Driven By</Label>
                <RadioGroup value={contactData.driven_by} onValueChange={v => setContactData(d => ({ ...d, driven_by: v }))} className="flex gap-4 mt-1">
                  {["Owner", "User", "Driver"].map(o => (
                    <div key={o} className="flex items-center gap-1.5"><RadioGroupItem value={o} id={`driven-${o}`} /><Label htmlFor={`driven-${o}`} className="text-sm">{o}</Label></div>
                  ))}
                </RadioGroup>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="form-label">Preferred Contact Mode</Label>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {["Phone", "SMS", "Email"].map(m => (
                      <label key={m} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox checked={contactData.preferred_contact_mode.includes(m)} onCheckedChange={c => setContactData(d => ({ ...d, preferred_contact_mode: c ? [...d.preferred_contact_mode, m] : d.preferred_contact_mode.filter(x => x !== m) }))} />
                        {m}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="form-label">Preferred Contact Time</Label>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {["Morning", "Afternoon", "Evening"].map(t => (
                      <label key={t} className="flex items-center gap-1.5 text-sm cursor-pointer">
                        <Checkbox checked={contactData.preferred_contact_time.includes(t)} onCheckedChange={c => setContactData(d => ({ ...d, preferred_contact_time: c ? [...d.preferred_contact_time, t] : d.preferred_contact_time.filter(x => x !== t) }))} />
                        {t}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Other Details */}
          {step === 3 && (
            <div className="space-y-5 py-2" data-testid="wizard-step-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-gray-400">Service Type *</Label>
                  <Select value={otherData.service_type} onValueChange={v => setOtherData(d => ({ ...d, service_type: v, pms_interval: "" }))}>
                    <SelectTrigger className="rounded-sm" data-testid="service-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>
                      {(settings?.service_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {otherData.service_type === "PMS" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-wider text-gray-400">PMS Interval *</Label>
                    <Select value={otherData.pms_interval} onValueChange={v => setOtherData(d => ({ ...d, pms_interval: v }))}>
                      <SelectTrigger className="rounded-sm" data-testid="pms-interval-select"><SelectValue placeholder="Select interval" /></SelectTrigger>
                      <SelectContent>
                        {PMS_INTERVALS.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-gray-400">Service Advisor *</Label>
                  <Select value={otherData.allocated_sa} onValueChange={v => setOtherData(d => ({ ...d, allocated_sa: v }))}>
                    <SelectTrigger className="rounded-sm" data-testid="advisor-select"><SelectValue placeholder="Assign SA" /></SelectTrigger>
                    <SelectContent>
                      {(settings?.service_advisors || []).map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-gray-400">Current KM *</Label>
                  <Input type="number" min="0" className="rounded-sm" value={otherData.current_km}
                    onChange={e => setOtherData(d => ({ ...d, current_km: e.target.value }))}
                    data-testid="current-km-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-gray-400">Specific Repair Request *</Label>
                <Textarea className="rounded-sm" rows={4} placeholder="Describe any specific issues or requests..."
                  value={otherData.specific_repair_request}
                  onChange={e => setOtherData(d => ({ ...d, specific_repair_request: e.target.value }))}
                  data-testid="repair-request-input" />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {[
                  ["ots_recall", "OTS/Recall", "Renault recall check"],
                  ["priority_customer", "Priority", "High priority customer"],
                  ["recovered_lost_customer", "Recovered", "From lost customer list"],
                ].map(([key, title, sub]) => (
                  <div key={key} className="flex items-center justify-between p-4 border border-gray-200 rounded-sm">
                    <div>
                      <p className="text-sm font-medium">{title}</p>
                      <p className="text-xs text-gray-500">{sub}</p>
                    </div>
                    <Switch checked={otherData[key]}
                      onCheckedChange={v => setOtherData(d => ({ ...d, [key]: v }))}
                      data-testid={`switch-${key}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: Documents */}
          {step === 4 && (
            <div className="space-y-6 py-2" data-testid="wizard-step-4">
              <DocBlock
                title="Insurance Document"
                file={docFiles.insurance}
                onPick={f => { setDocFiles(x => ({ ...x, insurance: f })); setDocs(d => ({ ...d, insurance_attached: !!f })); }}
                testIdPrefix="ins"
              />
              <DocBlock
                title="Registration (RC) Document"
                file={docFiles.rc}
                onPick={f => { setDocFiles(x => ({ ...x, rc: f })); setDocs(d => ({ ...d, rc_attached: !!f })); }}
                testIdPrefix="rc"
              />
            </div>
          )}

          <DialogFooter className="flex justify-between gap-2 pt-2">
            <Button variant="outline" className="rounded-sm" disabled={step === 1} onClick={() => setStep(s => s - 1)} data-testid="wizard-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < 4 ? (
              <Button className="rounded-sm bg-black text-white hover:bg-gray-800" 
                disabled={(step === 1 && !canGoNext1) || (step === 2 && !receptionTime) || (step === 3 && !canGoNext3)} 
                onClick={handleWizardNext} data-testid="wizard-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button className="rounded-sm bg-black text-white hover:bg-gray-800" onClick={handleSave} disabled={savingEntry} data-testid="wizard-save">
                {savingEntry ? "Saving..." : "Save Entry"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filter Dialog */}
      <DiscardConfirm
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        onDiscard={() => {
          setDiscardConfirmOpen(false);
          setWizardOpen(false);
          resetWizard();
        }}
      />

      <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
              Filters
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Branch
              </Label>
              <Select value={filters.branch || "all"} onValueChange={(v) => setFilters(f => ({ ...f, branch: v === "all" ? "" : v }))}>
                <SelectTrigger className="rounded-sm" data-testid="filter-branch">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {branches.map(b => <SelectItem key={b.branch_id || b.name} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Date Filter
              </Label>
              <Select value={filters.date_filter} onValueChange={(v) => setFilters(f => ({ ...f, date_filter: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="filter-date">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{DATE_FILTERS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {filters.date_filter === "custom" && (
              <>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">From Date</Label>
                  <Input type="date" value={filters.date_from} onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))} className="rounded-sm" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">To Date</Label>
                  <Input type="date" value={filters.date_to} onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))} className="rounded-sm" />
                </div>
              </>
            )}

            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Source
              </Label>
              <Select value={filters.source || "all"} onValueChange={(v) => setFilters(f => ({ ...f, source: v === "all" ? "" : v }))}>
                <SelectTrigger className="rounded-sm" data-testid="filter-source">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
                Status
              </Label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                <SelectTrigger className="rounded-sm" data-testid="filter-status">
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => {
                setFilters({ branch: "", date_filter: "today", source: "", status: "", date_from: "", date_to: "" });
              }}
              data-testid="reception-filter-clear"
            >
              Clear All
            </Button>
            <Button
              className="bg-black text-white hover:bg-gray-800 rounded-sm"
              onClick={() => setFilterOpen(false)}
              data-testid="reception-filter-apply"
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customize + Rearrange modals (shared) */}
      <ColumnModals
        columnManager={columnManager}
        defaultColumns={RECEPTION_COLUMNS}
        customizableIds={CUSTOMIZABLE_RECEPTION_COLUMN_IDS}
      />
    </div>
  );
}

// Helper: Input field with old masked value
const FieldWithOld = ({ label, value, oldValue, onChange, testId, mask }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input value={value} onChange={e => onChange(e.target.value)} className="rounded-sm" data-testid={testId} />
    {oldValue && <p className="text-[10px] text-gray-400 mt-0.5 ">{mask(oldValue)}</p>}
  </div>
);

// Helper: Document upload block (simplified - no "Not Collected" checkbox)
const DocBlock = ({ title, file, onPick, testIdPrefix }) => {
  const inputRef = useRef(null);
  return (
    <div className="p-4 border border-gray-200 rounded-sm space-y-3">
      <p className="text-sm font-bold">{title}</p>
      <div className="flex items-center gap-3">
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          className="hidden"
          onChange={e => { onPick(e.target.files?.[0] || null); e.target.value = ""; }}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <Button variant={file ? "default" : "outline"} size="sm" className="rounded-sm"
          onClick={() => inputRef.current?.click()} data-testid={`${testIdPrefix}-attach-btn`}>
          {file ? <><FileCheck className="w-4 h-4 mr-1" /> Attached</> : <><Paperclip className="w-4 h-4 mr-1" /> Attach</>}
        </Button>
        {file ? (
          <>
            <span className="text-xs text-gray-600 truncate max-w-[220px]">{file.name}</span>
            <button type="button" className="text-xs text-gray-400 underline"
              onClick={() => onPick(null)} data-testid={`${testIdPrefix}-remove-btn`}>Remove</button>
          </>
        ) : (
          <span className="text-xs text-gray-400">Optional</span>
        )}
      </div>
    </div>
  );
};
